import React, {useCallback, useEffect,useState} from 'react';

import {
    Box,
    ClickAwayListener,
    Slider,
    Stack,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

const styles = {
    holder: {
        padding: '20px 20px 0 20px',
        border: '1px solid #666',
        borderRadius: '10px',
        backgroundColor: '#444',
        marginBottom: 2
    },
};

const ColorPicker = ({
    label = 'Primary color',
    value = '#3A61D7',
    onChange,
    sx = {}
}) => {
    const theme = useTheme();

    const [showColorPicker, setShowColorPicker] = useState(false);

    // Convert hex to HSV
    const hexToHsv = useCallback((hex) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const diff = max - min;

        let h = 0;
        if (diff !== 0) {
            if (max === r) h = ((g - b) / diff) % 6;
            else if (max === g) h = (b - r) / diff + 2;
            else h = (r - g) / diff + 4;
        }

        h = Math.round(h * 60);
        if (h < 0) h += 360;

        const s = max === 0 ? 0 : diff / max;
        const v = max;

        return {h, s: s * 100, v: v * 100};
    }, []);

    // Convert HSV to hex
    const hsvToHex = useCallback((h, s, v) => {
        s /= 100;
        v /= 100;

        const c = v * s;
        const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
        const m = v - c;

        let r, g, b;
        if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
        else if (h >= 60 && h < 120) [r, g, b] = [x, c, 0];
        else if (h >= 120 && h < 180) [r, g, b] = [0, c, x];
        else if (h >= 180 && h < 240) [r, g, b] = [0, x, c];
        else if (h >= 240 && h < 300) [r, g, b] = [x, 0, c];
        else [r, g, b] = [c, 0, x];

        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);

        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }, []);

    const [hsv, setHsv] = useState(() => hexToHsv(value));
    const [hexInput, setHexInput] = useState(value.toUpperCase());

    useEffect(() => {
        const newHsv = hexToHsv(value);
        setHsv(newHsv);
        setHexInput(value.toUpperCase());
    }, [value, hexToHsv]);

    const updateColor = useCallback((newHsv) => {
        setHsv(newHsv);
        const newHex = hsvToHex(newHsv.h, newHsv.s, newHsv.v);
        setHexInput(newHex.toUpperCase());
        onChange?.(newHex);
    }, [hsvToHex, onChange]);

    const handleSaturationChange = (event, newValue) => {
        updateColor({...hsv, s: newValue});
    };

    const handleValueChange = (event, newValue) => {
        updateColor({...hsv, v: newValue});
    };

    const handleHueChange = (event, newValue) => {
        updateColor({...hsv, h: newValue});
    };

    const handleHexChange = (event) => {
        const newHex = event.target.value;
        setHexInput(newHex);

        if (/^#[0-9A-Fa-f]{6}$/.test(newHex)) {
            const newHsv = hexToHsv(newHex);
            setHsv(newHsv);
            onChange?.(newHex);
        }
    };

    const handleClickAway = () => {

        setShowColorPicker(false);
    };

    const saturationGradient = `linear-gradient(to right, 
        ${hsvToHex(hsv.h, 0, hsv.v)}, 
        ${hsvToHex(hsv.h, 100, hsv.v)})`;

    const valueGradient = `linear-gradient(to right, 
        ${hsvToHex(hsv.h, hsv.s, 0)}, 
        ${hsvToHex(hsv.h, hsv.s, 100)})`;

    const hueGradient = 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)';

    return (
        <Box sx={{width: '100%', ...sx}}>

            {label && <Typography color={theme.palette.white?.main} sx={{mb: 2}}>
                {label}
            </Typography>}

            {showColorPicker && (
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Box sx={styles.holder}>
                        <Box sx={{mb: 2}}>
                            <Box
                                sx={{
                                    height: 20,
                                    borderRadius: 1,
                                    background: saturationGradient,
                                    position: 'relative',
                                    mb: 1
                                }}
                            >
                                <Slider
                                    value={hsv.s}
                                    onChange={handleSaturationChange}
                                    min={0}
                                    max={100}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        color: 'transparent',
                                        '& .MuiSlider-thumb': {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: '#fff',
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                            }
                                        },
                                        '& .MuiSlider-track': {
                                            display: 'none'
                                        },
                                        '& .MuiSlider-rail': {
                                            display: 'none'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{mb: 2}}>
                            <Box
                                sx={{
                                    height: 20,
                                    borderRadius: 1,
                                    background: valueGradient,
                                    position: 'relative',
                                    mb: 1
                                }}
                            >
                                <Slider
                                    value={hsv.v}
                                    onChange={handleValueChange}
                                    min={0}
                                    max={100}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        color: 'transparent',
                                        '& .MuiSlider-thumb': {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: '#fff',
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                            }
                                        },
                                        '& .MuiSlider-track': {
                                            display: 'none'
                                        },
                                        '& .MuiSlider-rail': {
                                            display: 'none'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{mb: 2}}>
                            <Box
                                sx={{
                                    height: 20,
                                    borderRadius: 1,
                                    background: hueGradient,
                                    position: 'relative',
                                    mb: 1
                                }}
                            >
                                <Slider
                                    value={hsv.h}
                                    onChange={handleHueChange}
                                    min={0}
                                    max={360}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        color: 'transparent',
                                        '& .MuiSlider-thumb': {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: '#fff',
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                            }
                                        },
                                        '& .MuiSlider-track': {
                                            display: 'none'
                                        },
                                        '& .MuiSlider-rail': {
                                            display: 'none'
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </ClickAwayListener>
            )}

            <Stack direction="row" spacing={2} alignItems="center" onClick={() => setShowColorPicker(!showColorPicker)}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: value,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        cursor: 'pointer'
                    }}

                />

                <TextField
                    value={hexInput}
                    onChange={handleHexChange}
                    sx={{
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            fontFamily: 'monospace'
                        }
                    }}
                />
            </Stack>

        </Box>
    );
};

export default ColorPicker;